import React, { useState, useEffect, useRef } from "react";
import { Table, message, Tooltip, Button, Tag } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { api, API_BASE_URL } from "../../config/api";
import { useMyContext } from "../../context/MyContext";

const CurrencyHistory = ({ setShowHistory }) => {
  const { token } = useMyContext();
  const [historyData, setHistoryData] = useState([]);
  const [latestRates, setLatestRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState(null);
  const fetchCalled = useRef(false);

  useEffect(() => {
    if (!fetchCalled.current) {
      fetchCalled.current = true;
      fetchHistoryData();
      fetchLatestRates();
    }
  }, []);

  const fetchHistoryData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${API_BASE_URL}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200) {
        setHistoryData(response.data.data);
      } else {
        message.error("Failed to fetch currency history.");
      }
    } catch (error) {
      message.error("Server error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestRates = async () => {
    try {
      const response = await api.get(`${API_BASE_URL}/history/oldRates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 200 && response.data.success) {
        setLatestRates(response.data.data);
      } else {
        message.error("Failed to fetch latest exchange rates.");
      }
    } catch (error) {
      message.error(error.response.data.message);
    }
  };

  // Function to manually sort data by exchange rate
  const handleSort = () => {
    const newOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newOrder);

    const sortedData = [...historyData].sort((a, b) => {
      const rateA = parseFloat(a.exchangeRate);
      const rateB = parseFloat(b.exchangeRate);
      return newOrder === "asc" ? rateA - rateB : rateB - rateA;
    });

    setHistoryData(sortedData);
  };

  const columns = [
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      key: "updatedBy",
    },
    {
      title: "Base Currency",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          <strong>{text}</strong> ({record.symbol})
        </span>
      ),
    },
    {
      title: "Target Currency",
      dataIndex: "targetCurrency",
      key: "targetCurrency",
      render: (text) => {
        const symbols = { USD: '$', EUR: '€', GBP: '£' };
        return (
          <span>
            <strong>{text}</strong> ({symbols[text]})
          </span>
        );
      },
    },
    {
      title: (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <span>Exchange Rate</span>
            <Tooltip
              title={
                <>
                  <div>Current Exchange Rates:</div>
                  {Array.isArray(latestRates) && latestRates.length > 0 ? (
                    latestRates.map(({ fromCurrency, fromSymbol, toCurrency, toSymbol, exchangeRate }) => (
                      <div key={`${fromCurrency}-${toCurrency}`}>
                        {fromCurrency} ({fromSymbol}) → {toCurrency} ({toSymbol}): {exchangeRate.toFixed(4)}
                      </div>
                    ))
                  ) : (
                    <div>No exchange rates available</div>
                  )}
                </>
              }
            >
              <InfoCircleOutlined
                style={{ color: "#1890ff", marginLeft: 8, cursor: "pointer" }}
              />
            </Tooltip>
          </div>
          <button
            onClick={handleSort}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "16px",
              cursor: "pointer",
            }}
          >
            {sortOrder === "asc" ? "↓" : "↑"}
          </button>
        </div>
      ),
      dataIndex: "exchangeRate",
      key: "exchangeRate",
      render: (rate) => (
        <Tag color="green">
          {typeof rate === 'number' ? rate.toFixed(4) : rate}
        </Tag>
      ),
    },
    {
      title: "From Date",
      dataIndex: "fromDate",
      key: "fromDate",
      sorter: (a, b) => new Date(a.fromDate) - new Date(b.fromDate),
      render: (text) => dayjs(text).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "To Date",
      dataIndex: "toDate",
      key: "toDate",
      sorter: (a, b) => new Date(a.toDate) - new Date(b.toDate),
      render: (text) => dayjs(text).format("YYYY-MM-DD HH:mm"),
    },
  ];

  return (
    <div className="w-full p-4 bg-white rounded-md shadow">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => setShowHistory(false)}
          className="px-4 py-2 btnBg flex items-center rounded-[5px]"
        >
          <span className="mr-2">←</span> Go Back
        </button>
        <h2 className="text-lg font-semibold flex-1 text-center">
          Currency Exchange Rate History
        </h2>
      </div>

      <div className="mb-4 p-3 bg-blue-50 rounded-md">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> This shows the historical exchange rates between different currency pairs.
          Each row represents one exchange rate from a base currency to a target currency during a specific time period.
        </p>
      </div>

      <Table
        columns={columns}
        dataSource={historyData.map((item, index) => ({
          ...item,
          key: item.key || `${item._id}_${index}`,
        }))}
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 800 }}
      />
    </div>
  );
};

export default CurrencyHistory;